import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import EventCard from '../components/EventCard';
import Skeleton from '../components/Skeleton';

const PAGE_SIZE = 12;

export default function Home() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    api.getEvents({ limit: PAGE_SIZE })
      .then(data => {
        setEvents(data.items || data);
        setCursor(data.cursor || null);
        setHasMore(!!data.cursor);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const data = await api.getEvents({ cursor, limit: PAGE_SIZE });
      setEvents(prev => [...prev, ...(data.items || [])]);
      setCursor(data.cursor || null);
      setHasMore(!!data.cursor);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, hasMore, loadingMore]);

  if (loading) {
    return (
      <div>
        <div className="mb-8">
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton.Card key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{t('common.errorLoading')}</p>
        <button onClick={() => window.location.reload()} className="btn-secondary">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (events.length === 0) {
    const steps = [
      {
        icon: (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
          </svg>
        ),
        title: t('home.step1Title'),
        description: t('home.step1Desc')
      },
      {
        icon: (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
        ),
        title: t('home.step2Title'),
        description: t('home.step2Desc')
      },
      {
        icon: (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        title: t('home.step3Title'),
        description: t('home.step3Desc')
      }
    ];

    return (
      <div className="py-8 px-4">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-brand-100 to-brand-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-brand-100">
            <svg className="w-10 h-10 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            {t('home.noEvents')}
          </h1>
          <p className="text-gray-500 max-w-md mx-auto text-lg">
            {t('home.noEventsDesc')}
          </p>
        </div>

        {/* How It Works */}
        <div className="max-w-3xl mx-auto mb-12">
          <h2 className="text-center text-sm font-semibold text-brand-600 uppercase tracking-wide mb-6">
            {t('home.howItWorks')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="card p-6 text-center h-full hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-brand-100 text-brand-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    {step.icon}
                  </div>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 bg-brand-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-500">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/organizer/onboard"
            className="btn-primary inline-flex items-center gap-2 px-8 py-4 text-lg"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('home.createFirst')}
          </Link>
          <p className="text-sm text-gray-400 mt-4">
            {t('home.noCreditCard')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {t('home.title')}
        </h1>
        <p className="text-gray-500 mt-1">
          {t('home.subtitle')}
        </p>
      </div>

      {/* Event Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="text-center mt-8">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="btn-secondary inline-flex items-center gap-2 px-6 py-3"
          >
            {loadingMore ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                {t('common.loading')}
              </>
            ) : (
              t('home.loadMore')
            )}
          </button>
        </div>
      )}

      {/* Organizer CTA */}
      <div className="mt-16 text-center py-12 px-6 bg-gradient-to-br from-brand-50 to-brand-100 rounded-2xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('home.ctaTitle')}
        </h2>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          {t('home.ctaDesc')}
        </p>
        <Link
          to="/organizer/onboard"
          className="btn-primary inline-flex items-center gap-2 px-6 py-3"
        >
          {t('home.ctaButton')}
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
